/**
 * Versioned telemetry SPAN taxonomy — `telemetry-spans-v1` (DEVOP-5 / ADR 0074, IDEA-76 / A8).
 *
 * A8 makes every operation a **durable, structured span keyed on correlation id**, emitted from
 * BOTH the browser and the gateway into the DEVOP-5 observability platform. This file is the ONE
 * shared source of those span shapes + their builders onto the platform's existing wire shapes
 * (`TelemetryEventInput` / `MetricPointInput`, `./observability-v1.js`), so producer and platform
 * parse the same thing (ADR 0001/0005). Builders return objects that PASS the wire schemas, so the
 * platform ingests them unchanged — no platform migration.
 *
 * ADR 0032: spans are **telemetry on a separate channel**, never a domain event per command. The
 * op-typed cost span here GENERALISES the llm-only tracking (the `llm.cost.v1` domain event stays a
 * BROWSER domain event, unchanged — this is the observability-channel generalisation, not a rename).
 */
import { z } from 'zod';
import {
  type TelemetryEventInput,
  type MetricPointInput,
  type TelemetryLevel,
} from './observability-v1.js';

/** Contract namespace label (parity with the sibling observability-v1 label). */
export const TELEMETRY_SPANS_V1_VERSION = 'telemetry-spans-v1' as const;

/** The fixed `system` label every idea-engine span carries into the unified sink. */
export const IDEA_ENGINE_SYSTEM = 'idea-engine' as const;

// ── Op-typed cost span (generalises llm.cost.v1 / the llm.call metric) ────────
//
// One cost span shape with an `op`/`kind` discriminator so evidence search/fetch + data pulls stop
// being invisible (today only `llm` cost is tracked). Model-spend ops (llm/embed) carry token usage
// and a real `costUsd`, and still emit the back-compat `llm.call` metric the platform cost pane
// aggregates (ADR 0080); non-model ops (search/fetch/data) carry latency with `costUsd` 0 and emit a
// generic `cost` point — durable + queryable, so a slow data pull is finally observable.

export const COST_OP_KINDS = ['llm', 'embed', 'search', 'fetch', 'data'] as const;
export const CostOpKindSchema = z.enum(COST_OP_KINDS);
export type CostOpKind = z.infer<typeof CostOpKindSchema>;

/** The op kinds that are real model spend — they keep the `llm.call` metric name (cost-pane back-compat). */
export const MODEL_SPEND_OP_KINDS: readonly CostOpKind[] = ['llm', 'embed'];

export const CostSpanV1Schema = z.object({
  /** The op discriminator (the taxonomy's `kind` axis). */
  op: CostOpKindSchema,
  /** The specific route operation, e.g. `generate:tam` | `evidence:search` | `evidence:extract`. */
  label: z.string().min(1).max(80),
  /** Spend in USD (0 for non-model ops — their value is the latency, not a bill). */
  costUsd: z.number().finite().nonnegative(),
  durationMs: z.number().finite().nonnegative().optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional(),
  model: z.string().min(1).max(120).optional(),
  provider: z.string().min(1).max(64).optional(),
  /** The correlation id this spend joins to (metric points key correlation via a label). */
  correlationId: z.string().min(1).max(120).optional(),
  ownerId: z.string().min(1).max(120).optional(),
  ideaId: z.string().min(1).max(120).optional(),
});
export type CostSpanV1 = z.infer<typeof CostSpanV1Schema>;

/**
 * Build one metric point from an op-typed cost span. Model-spend ops (llm/embed) → the back-compat
 * `llm.call` point the cost pane aggregates (value = USD, unit = usd, `{model,provider,op}` labels);
 * non-model ops → a generic `cost` point (value = USD, still `usd`) so search/fetch/data become
 * queryable. `correlationId`/`ideaId`/`durationMs`/tokens ride the labels (metric points have no
 * top-level correlation field). `atMs` is left for the platform to stamp.
 */
export function costSpanToMetric(span: CostSpanV1): MetricPointInput {
  const modelSpend = MODEL_SPEND_OP_KINDS.includes(span.op);
  const labels: Record<string, unknown> = {
    op: span.label,
    kind: span.op,
    model: span.model ?? 'unknown',
    provider: span.provider ?? 'unknown',
  };
  if (span.inputTokens !== undefined) labels['inputTokens'] = span.inputTokens;
  if (span.outputTokens !== undefined) labels['outputTokens'] = span.outputTokens;
  if (span.durationMs !== undefined) labels['durationMs'] = span.durationMs;
  if (span.correlationId !== undefined) labels['correlationId'] = span.correlationId;
  if (span.ideaId !== undefined) labels['ideaId'] = span.ideaId;
  return {
    system: IDEA_ENGINE_SYSTEM,
    name: modelSpend ? 'llm.call' : 'cost',
    value: span.costUsd,
    unit: 'usd',
    labels,
    ...(span.ownerId !== undefined ? { ownerId: span.ownerId } : {}),
  };
}

// ── Command span (browser — from A2's onLifecycle) ───────────────────────────

export const COMMAND_SPAN_STATUSES = ['no-handler', 'completed', 'failed'] as const;
export const CommandSpanStatusSchema = z.enum(COMMAND_SPAN_STATUSES);
export type CommandSpanStatus = z.infer<typeof CommandSpanStatusSchema>;

export const CommandSpanV1Schema = z.object({
  /** The dispatch id (=== the ack's commandId) — the command's correlation key. */
  commandId: z.string().min(1).max(120),
  name: z.string().min(1).max(120),
  status: CommandSpanStatusSchema,
  durationMs: z.number().finite().nonnegative().optional(),
  errorKind: z.string().min(1).max(40).optional(),
  errorMessage: z.string().max(500).optional(),
});
export type CommandSpanV1 = z.infer<typeof CommandSpanV1Schema>;

/** Build the telemetry event for one command span (correlationId = the commandId). */
export function commandSpanToEvent(span: CommandSpanV1, source = 'web'): TelemetryEventInput {
  const level: TelemetryLevel = span.status === 'failed' ? 'error' : span.status === 'no-handler' ? 'warn' : 'info';
  const attributes: Record<string, unknown> = { command: span.name, status: span.status };
  if (span.durationMs !== undefined) attributes['durationMs'] = span.durationMs;
  if (span.errorKind !== undefined) attributes['errorKind'] = span.errorKind;
  const message = `command ${span.name} ${span.status}${span.errorMessage ? `: ${span.errorMessage}` : ''}`.slice(0, 4000);
  return {
    system: IDEA_ENGINE_SYSTEM,
    source,
    kind: 'command',
    level,
    correlationId: span.commandId,
    message,
    attributes,
  };
}

// ── API-call span (browser — promoted from the diagnostics-only ApiCallLog) ──

export const ApiCallSpanV1Schema = z.object({
  method: z.string().min(1).max(10),
  path: z.string().min(1).max(200),
  status: z.number().int().optional(),
  durationMs: z.number().finite().nonnegative(),
  ok: z.boolean(),
  errorKind: z.string().min(1).max(40).optional(),
  simulated: z.boolean().optional(),
  correlationId: z.string().min(1).max(120).optional(),
});
export type ApiCallSpanV1 = z.infer<typeof ApiCallSpanV1Schema>;

/** Build the telemetry event for one API-call span (correlationId threads from the api-client). */
export function apiCallSpanToEvent(span: ApiCallSpanV1, source = 'web'): TelemetryEventInput {
  const attributes: Record<string, unknown> = { method: span.method, path: span.path, durationMs: span.durationMs, ok: span.ok };
  if (span.status !== undefined) attributes['status'] = span.status;
  if (span.errorKind !== undefined) attributes['errorKind'] = span.errorKind;
  if (span.simulated !== undefined) attributes['simulated'] = span.simulated;
  const outcome = span.status !== undefined ? String(span.status) : span.ok ? 'ok' : span.errorKind ?? 'error';
  return {
    system: IDEA_ENGINE_SYSTEM,
    source,
    kind: 'api.call',
    level: span.ok ? 'info' : 'error',
    ...(span.correlationId !== undefined ? { correlationId: span.correlationId } : {}),
    message: `${span.method} ${span.path} → ${outcome}`.slice(0, 4000),
    attributes,
  };
}

// ── Run / task span (gateway — it owns run state) ────────────────────────────

export const RUN_SPAN_PHASES = ['enqueued', 'leased', 'completed', 'failed', 'cancelled'] as const;
export const RunSpanPhaseSchema = z.enum(RUN_SPAN_PHASES);
export type RunSpanPhase = z.infer<typeof RunSpanPhaseSchema>;

export const RunSpanV1Schema = z.object({
  runId: z.string().min(1).max(120),
  runKind: z.string().min(1).max(120),
  phase: RunSpanPhaseSchema,
  /** Present ⇒ a TASK span (`task.<phase>`); absent ⇒ a RUN span (`run.<phase>`). */
  taskId: z.string().min(1).max(120).optional(),
  taskKind: z.string().min(1).max(120).optional(),
  correlationId: z.string().min(1).max(120).optional(),
  ownerId: z.string().min(1).max(120).optional(),
  ideaId: z.string().min(1).max(120).optional(),
  durationMs: z.number().finite().nonnegative().optional(),
  initiator: z.string().min(1).max(120).optional(),
});
export type RunSpanV1 = z.infer<typeof RunSpanV1Schema>;

/** Build the telemetry event for one run/task span (keyed on the run's correlation id). */
export function runSpanToEvent(span: RunSpanV1, source = 'gateway'): TelemetryEventInput {
  const attributes: Record<string, unknown> = { runKind: span.runKind };
  if (span.taskKind !== undefined) attributes['taskKind'] = span.taskKind;
  if (span.ideaId !== undefined) attributes['ideaId'] = span.ideaId;
  if (span.initiator !== undefined) attributes['initiator'] = span.initiator;
  if (span.durationMs !== undefined) attributes['durationMs'] = span.durationMs;
  const kind = span.taskId !== undefined ? `task.${span.phase}` : `run.${span.phase}`;
  return {
    system: IDEA_ENGINE_SYSTEM,
    source,
    kind,
    level: span.phase === 'failed' ? 'error' : 'info',
    runId: span.runId,
    ...(span.taskId !== undefined ? { taskId: span.taskId } : {}),
    ...(span.correlationId !== undefined ? { correlationId: span.correlationId } : {}),
    ...(span.ownerId !== undefined ? { ownerId: span.ownerId } : {}),
    message: `${kind} ${span.runKind}`.slice(0, 4000),
    attributes,
  };
}
